import React, {useState, useEffect} from "react";
import {
    Box,
    Text,
    Textarea,
    Button,
    useColorModeValue,
    useToast,
    Flex,
    Spinner,
    Stack
} from "@chakra-ui/react";
import {cqAPI} from "../../lib/Auth";

const EzraPrompts = () => {
    const bgColor = useColorModeValue("gray.100", "gray.700");
    const cardBg = useColorModeValue("#ffffff", "#212330");
    const borderClr = useColorModeValue("gray.200", "gray.600");
    const sectionBg = useColorModeValue("gray.50", "gray.600");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const defaultFormData = {
        // Shared (voice + text)
        moderator_prompt: "",
        data_extractor_prompt: {intro: "", guidelines: ""},
        security_prompt: "",
        emergency_prompt: "",
        summarizer_base_prompt: "",
        summarizer_edge_cases: "",
        // Text-only
        note_creator_prompt: "",
        chat_session_detection_prompt: "",
        chat_session_summarizer_prompt: "",
        followup_prompt: "",
        verification_prompt: "",
        // Voice-only
        security_prompt_transfer: "",
        demo_mode_section: "",
    };

    const [formData, setFormData] = useState(defaultFormData);
    const toast = useToast();

    // ----------- LOAD PROMPTS -----------
    useEffect(() => {
        const fetchPrompts = async () => {
            setLoading(true);
            try {
                const response = await cqAPI.get("/prompts/ezra");
                if (response.type === "ok" && response.data) {
                    setFormData(response.data);
                } else {
                    console.warn("Invalid or missing response data, using defaults");
                    setFormData(defaultFormData);
                    toast({
                        title: "Warning",
                        description: "Loaded default Ezra Prompts because the API response was invalid.",
                        status: "warning",
                        duration: 3000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error("Error loading Ezra Prompts:", error);
                toast({
                    title: "Error",
                    description: "Failed to load Ezra Prompts.",
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };
        fetchPrompts();
    }, []);

    // ----------- SAVE PROMPTS -----------
    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await cqAPI.put("/prompts/ezra", formData);
            if (response.type === "ok") {
                toast({
                    title: "Saved",
                    description: "Ezra Prompts updated successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true
                });
            } else {
                toast({
                    title: "Error",
                    description: response.msg || 'Failed to save!',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error saving Ezra Prompts:", error);
            toast({
                title: "Error",
                description: "Failed to save Ezra Prompts.",
                status: "error",
                duration: 3000,
                isClosable: true
            });
        } finally {
            setSaving(false);
        }
    };

    // ----------- FIELD RENDERER -----------
    const renderField = (label, fieldName, placeholder) => (
        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
            <Text fontWeight="semibold" mb={2}>{label}</Text>
            <Textarea
                minH="120px"
                resize="vertical"
                value={formData[fieldName] || ""}
                onChange={(e) => setFormData({...formData, [fieldName]: e.target.value})}
                placeholder={placeholder || `${label}...`}
            />
        </Box>
    );

    const renderNestedField = (parentLabel, parentKey, fields) => (
        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
            <Text fontWeight="bold" mb={3}>{parentLabel}</Text>
            <Stack spacing={3}>
                {fields.map(({label, key}) => (
                    <Box key={key} bg={sectionBg} p={3} borderRadius="md">
                        <Text fontWeight="semibold" mb={1} fontSize="sm">{label}</Text>
                        <Textarea
                            minH="100px"
                            resize="vertical"
                            value={(formData[parentKey] && formData[parentKey][key]) || ""}
                            onChange={(e) => setFormData({
                                ...formData,
                                [parentKey]: {
                                    ...(formData[parentKey] || {}),
                                    [key]: e.target.value
                                }
                            })}
                            placeholder={`${label}...`}
                        />
                    </Box>
                ))}
            </Stack>
        </Box>
    );

    // ----------- RENDER UI -----------
    return (
        <Box bg={bgColor} minH="100dvh" w="100%">
            <Box bg={bgColor} p={6} borderRadius="md" w="100%" maxW="100%">
                <Flex justify="space-between" align="center" mb={6}>
                    <Text fontSize="xl" fontWeight="bold">Ezra Prompts</Text>
                    <Button
                        colorScheme="blue"
                        onClick={handleSave}
                        isLoading={saving}
                        loadingText="Saving"
                    >
                        Save
                    </Button>
                </Flex>

                {loading ? (
                    <Flex justify="center" align="center" h="200px">
                        <Spinner size="xl"/>
                    </Flex>
                ) : (
                    <Stack spacing={6}>
                        {/* ── Shared (voice + text) ── */}
                        <Text fontSize="lg" fontWeight="bold" color="blue.400">Shared (Voice + Text)</Text>

                        {renderField("Moderator Prompt", "moderator_prompt")}
                        {renderNestedField("Data Extractor Prompt", "data_extractor_prompt", [
                            {label: "Intro", key: "intro"},
                            {label: "Guidelines", key: "guidelines"},
                        ])}
                        {renderField("Security Prompt", "security_prompt")}
                        {renderField("Emergency Prompt", "emergency_prompt")}
                        {renderField("Summarizer — Base Prompt", "summarizer_base_prompt")}
                        {renderField("Summarizer — Edge Cases", "summarizer_edge_cases")}

                        {/* ── Text-only ── */}
                        <Text fontSize="lg" fontWeight="bold" color="green.400" mt={4}>Text-Only</Text>

                        {renderField("Note Creator Prompt", "note_creator_prompt")}
                        {renderField("Chat Session Detection Prompt", "chat_session_detection_prompt")}
                        {renderField("Chat Session Summarizer Prompt", "chat_session_summarizer_prompt")}
                        {renderField("Followup Prompt", "followup_prompt")}
                        {renderField("Verification Prompt", "verification_prompt")}

                        {/* ── Voice-only ── */}
                        <Text fontSize="lg" fontWeight="bold" color="purple.400" mt={4}>Voice-Only</Text>

                        {renderField("Security Prompt — Transfer Section", "security_prompt_transfer")}
                        {renderField("Demo Mode Section", "demo_mode_section")}
                    </Stack>
                )}
            </Box>
        </Box>
    );
};

export default EzraPrompts;
